/* eslint-disable no-param-reassign */
/* eslint no-shadow: ["error", { "allow": ["state"] }] */
import {
  omit, set, omitBy, isEmpty, isObject,
} from 'lodash-es';

import {
  getCompany as getCompanyById,
  updateCompany as _updateCompany,
  updateLogo as _uploadLogo,
} from '@/services/api/customer/company';

import { arrayBufferToBase64 } from '@/utils/file';

import {
  findSignature,
} from '@/services/api/document/document';

const cleanObject = (object) => omitBy(object,
  (elem) => {
    let shadow = elem;
    if (isObject(elem)) {
      shadow = cleanObject(elem);
    }
    return isEmpty(shadow) || shadow === '' || shadow === [] || shadow === {};
  });

function deepOmit(obj) {
  const finalObj = {};
  Object.keys(obj).forEach((key) => {
    if (obj[key] && typeof obj[key] === 'object') {
      if (Array.isArray(obj[key])) {
        const nestedObj = Object.values(deepOmit(obj[key]));
        if (nestedObj.length > 0) {
          finalObj[key] = nestedObj;
        }
      } else {
        const nestedObj = deepOmit(obj[key]);
        if (Object.keys(nestedObj).length) {
          finalObj[key] = nestedObj;
        }
      }
    } else if (obj[key] !== '' && obj[key] !== undefined && obj[key] !== null) {
      finalObj[key] = obj[key];
    }
  });
  return finalObj;
}

// initial state
const state = {
  siret: null,
  name: null,
  logo: null,
  logoUrl: null,
  address: {
    street: null,
    postalCode: null,
    city: null,
    country: null,
  },
  contact: {},
  revenue: null,
  companyId: null,
  codeNaf: null,
  formeJuridique: {},
  mainActivity: {},
  registrationDate: null,
  inCreation: null,
  signatures: {},
  signaturesJustCompleted: false,
};

// getters
const getters = {
  get: (state) => omit(state, 'signatures'),
  bankAccounts: (state) => state.bankAccounts,
  creditCards: (state) => state.creditCards,
  clean: (state) => cleanObject(state),
  revenue: (state) => state.revenue,
  iban: (state) => state.iban,
  signatures: (state) => state.signatures,
  signaturesJustCompleted: (state) => state.signaturesJustCompleted,
  mainActivity: (state) => state.mainActivity,
  getAddress: (state) => state.address,
  companyId: (state) => state.companyId,
};

// actions
const actions = {
  async getDocumentsToBeSigned({ commit }) {
    const success = await findSignature();
    let signatures = success.data;
    if (signatures && signatures.length > 0) {
      signatures = signatures
        .filter((signature) => signature.status === 'WAITING');
      commit('setValue', { signatures });
      return Promise.resolve(signatures.length > 0);
    }
    return Promise.resolve(false);
  },
  async uploadLogo({ state }) {
    try {
      const { logo } = state;
      const buffer = await logo.arrayBuffer();
      const fileByteArray = arrayBufferToBase64(buffer);
      const { data: company } = await _uploadLogo(
        {
          bytes: fileByteArray,
          contentType: logo.type,
          name: logo.name,
        },
        state.companyId,
      );
      return company;
    } catch (err) {
      return Promise.reject(err);
    }
  },
  async setCompanyById({ commit, dispatch }, companyId) {
    try {
      if (companyId) {
        const success = await getCompanyById(companyId);
        const company = success.data;
        commit('setCompany', company);
        commit('setValue', { companyId });
        return success.data.contact;
      }
      return Promise.resolve();
    } catch (err) {
      dispatch('reporting/sendError', { err, currentPage: 'Login' }, { root: true });
      return err;
    }
  },
  async setSelectedCompany({ commit, dispatch }, companyId) {
    try {
      if (companyId) {
        const success = await getCompanyById(companyId);
        const company = success.data;
        commit('setCompany', company);
        return success.data.contact;
      }
      return Promise.resolve();
    } catch (err) {
      dispatch('reporting/sendError', { err, currentPage: 'Login' }, { root: true });
      return err;
    }
  },
  /**
  ** Update company
  */
  async updateCompany({ state }, companyId) {
    await _updateCompany(deepOmit(omit(state, 'signatures')), companyId);
  },
  clearOne({ commit }) { commit('clear') },
  setValue({ commit }, object) {
    commit('setValue', object);
  },
  setValueWithPath({ commit }, object) {
    commit('setValueWithPath', object);
  },
  setCodeNaf({ commit }, codeNaf) {
    commit('setCodeNaf', codeNaf);
  },
  setMainActivity({ commit }, mainActivity) {
    commit('setMainActivity', mainActivity);
  },
  setSiretNotFound({ commit }, isNotFound) { commit('setSiretNotFound', isNotFound) },
  set({ commit }, company) { commit('setCompany', company) },
};

// mutations
const mutations = {
  clear(state) {
    Object.assign(state, {
      siret: '',
      id: '',
      name: '',
      address: {
        street: '',
        postalCode: '',
        city: '',
        country: '',
      },
      logo: '',
      contact: {},
      contacts: [],
      bankAccounts: [],
      creditCards: [],
      iban: '',
      isIbanValid: false,
      revenue: '',
      codeNaf: '',
      formeJuridique: '',
      mainActivity: {},
      customerType: 'ENTERPRISE',
      registrationDate: '',
      companyId: '',
      createdAt: '',
      advisors: '',
      oriasNumber: '',
      oriasStatus: '',
      companyType: '',
      inCreation: false,
      fundRaised: '',
      hasFundraised: false,
      stripePaymentExternalReference: '',
      stripePaymentExternalReferenceNonDeleg: '',
    });
  },
  setCompany(state, company) {
    Object.assign(state, { ...company });
  },
  setCodeNaf(state, codeNaf) {
    Object.assign(state, { codeNaf: { ...codeNaf } });
  },
  setMainActivity(state, mainActivity) {
    Object.assign(state, { mainActivity: { ...mainActivity } });
  },
  setValueWithPath(state, { value, path }) {
    set(state, path, value);
  },
  setValue(state, object) {
    const [key] = Object.keys(object);
    Object.assign(state, { [key]: object[key] });
    return Promise.resolve();
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
